/**
 * DI-5-3 — what this browser can do for a version's CLIENT nodes, and what
 * a version needs of it (lock `served-requirements-not-a-list`).
 *
 * A client node (workflows/v1 1.8.0, M5-NODE-00: `provider: 'client'`) runs
 * our code in the pilot's browser right after the approve and changes files
 * on the pilot's PC — the EXIF write-back, the SkySpecs export. What it needs
 * of the browser is SERVED per node instance (`clientRequirements`, `fsa` =
 * the File System Access API with a read-write folder handle, Chromium). The
 * drop page refuses an unsupported browser BEFORE the drop (lock
 * `refuse-before-the-drop`), never after the approve.
 *
 * Which node needs what is read off the served version, never a list kept
 * here: this file spells no node key (`locks.grep.test.ts`). A SHARED item is
 * served as its injection node only (DI-D59 — the grantee never sees the
 * owner's end node); its served `export` fact (workflows/v1 1.10.0, derived
 * by the gateway from the owner's FULL version) says the owner's version
 * holds a client end node, and every client end node needs `fsa`
 * (M5-NODE-00 §10.1.k) — so a non-`none` export stands for one such node.
 * Since workflows/v1 1.12.0 the injection node also serves
 * `versionClientRequirements` (the union over the owner's ENABLED client
 * nodes, same derivation) — preferred when present, because a version
 * carrying only a client node that is not an export (the sort into folders)
 * says `export: none` yet needs `fsa` (M6-NODE-SORT-01 §8.3 rule 8). Absent
 * (an older gateway) → the `export` rule above.
 *
 * The probe is a FEATURE check — no permission prompt, no picker call.
 */
import {
  supportsDirectoryPicker,
  supportsReadwriteHandles,
} from "../files/directoryPicker.js";
import {
  exportTitle,
  injectionFactsOf,
  versionClientRequirementsOf,
  type WorkflowNode,
  type WorkflowVersion,
} from "../../workflows/api/workflows.js";

export interface ClientCapabilities {
  /** `showDirectoryPicker` exists (Chromium; not Firefox / Safari). */
  fsa: boolean;
  /** …and a handle can be asked for read-write (`requestPermission`). */
  fsaReadwrite: boolean;
}

/** Every capability present — the pure helpers' default (no refusal). */
export const ALL_CAPABLE: ClientCapabilities = {
  fsa: true,
  fsaReadwrite: true,
};

/** This browser's capabilities — a feature probe, never a prompt. */
export function detectClientCapabilities(): ClientCapabilities {
  const fsa = supportsDirectoryPicker();
  return { fsa, fsaReadwrite: fsa && supportsReadwriteHandles() };
}

/** One client node of a version the browser cannot serve. */
export interface MissingRequirement {
  /** The served node key; null for a shared item's derived end node. */
  nodeKey: string | null;
  /** The words naming the node in the message (see `clientNodeTitle`). */
  title: string;
  /** The served requirements this browser lacks (an unknown one included). */
  missing: string[];
}

/** One client node of a version and what it needs (before the caps). */
export interface ClientNeed {
  nodeKey: string | null;
  title: string;
  requirements: string[];
}

/**
 * The words naming a client node. workflows/v1 serves no title on a node
 * instance (only admin/v1's definition list carries `ui.label`, and the web
 * app does not call admin/v1): the served key's human form, the last resort
 * DI-5-3 allows ("node.write_back_exif" → "Write back exif") — a rule over
 * the served string, never a key → title map.
 */
export function clientNodeTitle(node: Pick<WorkflowNode, "key">): string {
  const tail = node.key.includes(".")
    ? node.key.slice(node.key.indexOf(".") + 1)
    : node.key;
  const words = tail.replace(/[_.-]+/g, " ").trim();
  if (words.length === 0) return node.key;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function enabledNodes(
  version: Pick<WorkflowVersion, "nodes"> | null | undefined,
): WorkflowNode[] {
  const out: WorkflowNode[] = [];
  for (const n of version?.nodes ?? []) {
    if (!n.enabled) continue;
    out.push(n);
    for (const s of n.steps ?? []) if (s.enabled) out.push(s);
  }
  return out;
}

/**
 * The words for a SHARED item's client nodes the export fact does not name
 * (e.g. a sort-only version): the grantee never learns which nodes the owner
 * runs (DI-D59), only that some run on the PC. Christian may reword.
 */
export const SHARED_CLIENT_NODES_TITLE = "Steps that run on this PC";

/**
 * What the version's ENABLED nodes (steps of an enabled node included) need
 * of the browser, from their served `clientRequirements`. A version that
 * serves NO client node (a shared item: the end nodes are cut) → one need
 * from the injection node: its served `versionClientRequirements` (1.12.0)
 * when present, else a client `export` fact (`fsa`); named by the export's
 * words, or `SHARED_CLIENT_NODES_TITLE` when the export is `none`.
 */
export function clientNeedsOf(
  version: Pick<WorkflowVersion, "nodes"> | null | undefined,
): ClientNeed[] {
  const needs: ClientNeed[] = enabledNodes(version).flatMap((n) => {
    const reqs = Array.isArray(n.clientRequirements)
      ? (n.clientRequirements as readonly unknown[]).filter(
          (r): r is string => typeof r === "string",
        )
      : [];
    return reqs.length > 0
      ? [{ nodeKey: n.key, title: clientNodeTitle(n), requirements: reqs }]
      : [];
  });
  if (needs.length > 0) return needs;
  // the version serves its client nodes (own): their switches decided above
  const servesClientNodes = (version?.nodes ?? []).some(
    (n) => n.provider === "client",
  );
  if (servesClientNodes) return [];
  const exported = injectionFactsOf(version)?.export ?? "none";
  // 1.12.0 — the served union, when this gateway serves it
  const served = versionClientRequirementsOf(version);
  if (served !== null) {
    if (served.length === 0) return [];
    return [
      {
        nodeKey: null,
        title: exportTitle(exported) ?? SHARED_CLIENT_NODES_TITLE,
        requirements: served,
      },
    ];
  }
  if (exported === "none") return [];
  return [
    { nodeKey: null, title: exportTitle(exported), requirements: ["fsa"] },
  ];
}

/** Is one served requirement met? An unknown string never is (fail closed). */
function meets(requirement: string, caps: ClientCapabilities): boolean {
  if (requirement === "fsa") return caps.fsaReadwrite;
  return false;
}

/** The needs of `needs` this browser cannot meet. */
export function missingOfNeeds(
  needs: readonly ClientNeed[],
  caps: ClientCapabilities,
): MissingRequirement[] {
  return needs.flatMap((n) => {
    const missing = n.requirements.filter((r) => !meets(r, caps));
    return missing.length > 0
      ? [{ nodeKey: n.nodeKey, title: n.title, missing }]
      : [];
  });
}

/**
 * PURE — the version's client nodes whose served requirements this browser
 * lacks; `[]` = the version drops fine here (index and verdict always work).
 */
export function missingRequirements(
  version: Pick<WorkflowVersion, "nodes"> | null | undefined,
  caps: ClientCapabilities,
): MissingRequirement[] {
  return missingOfNeeds(clientNeedsOf(version), caps);
}

/** The titles of several refusals, once each, in order. */
export function missingTitles(
  missing: readonly MissingRequirement[],
): string[] {
  return [...new Set(missing.map((m) => m.title))];
}

// ── the words (one place; Christian may reword) ──────────────────────────

/** The orchestrator's words (DI-5-3 §0; Christian may reword). */
export const UNSUPPORTED_BROWSER_MESSAGE =
  "This workflow writes files on your PC after approval. Your browser cannot do that — use Chrome or Edge (Chromium) for this workflow.";

/** The mark on a choice that cannot be picked here. */
export const NEEDS_CHROMIUM_MARK = "needs Chrome or Edge";

/** The message as one line (a tooltip / `title`): the words, then the nodes. */
export function unsupportedBrowserLine(
  missing: readonly MissingRequirement[],
): string {
  const titles = missingTitles(missing);
  return titles.length > 0
    ? `${UNSUPPORTED_BROWSER_MESSAGE} (${titles.join(", ")})`
    : UNSUPPORTED_BROWSER_MESSAGE;
}
